// src/pages/DOA.jsx

import React from 'react';
import { LoadingSpinner } from '../App';
import { useDoaData } from '../hooks/useDoaData';
import { useSheetTableFilters } from '../hooks/useSheetTableFilters';
import { formatDoaHeader } from '../utils/helpers';
import {
  FilterToolbar,
  DataTableCard,
  PaginationBar,
  PageError,
  SheetDataTable,
  PAGE,
} from '../components/ui';

import HoverNav from '../components/ui/HoverNav';

const DOA_ROWS_PER_PAGE = 24;

export default function DOA() {
  const { data, headers, loading, refreshing, error, refreshData } = useDoaData();

  const {
    searchTerm,
    setSearchTerm,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    page,
    setPage,
    allDates,
    filteredData,
    paginatedData,
    totalPages,
    clearFilters,
  } = useSheetTableFilters(data, { rowsPerPage: DOA_ROWS_PER_PAGE });

  const renderDoaCell = (row, header) => {
    const value = row[header] || '';
    const text = String(value || '').trim();
    const lowerText = text.toLowerCase();

    const displayHeader = String(formatDoaHeader(header) || '').toLowerCase();
    const rawHeader = String(header || '').toLowerCase();

    const isRemarks =
      displayHeader === 'remarks' ||
      rawHeader.includes('remarks') ||
      rawHeader.includes('remark') ||
      rawHeader.includes('last updated');

    const isPass = /^(pass|passed)\b/i.test(text);
    const isFail = /^(fail|failed)\b/i.test(text);

    let color = text ? PAGE.colors.text : PAGE.colors.muted;
    let background = 'transparent';
    let fontWeight = 400;

    if (isPass) {
      color = '#16a34a';
      background = '#dcfce7';
      fontWeight = 800;
    }

    if (isFail) {
      color = '#dc2626';
      background = '#fee2e2';
      fontWeight = 800;
    }

    if (isRemarks && text) {
      color = '#dc2626';
      background = '#fee2e2';
      fontWeight = 800;
    }

    return (
      <td
        key={header}
        style={{
          ...PAGE.type.tableCell,
          color,
          fontWeight,
          whiteSpace: 'nowrap',
          padding: '8px 12px',
          fontSize: 12,
          transition: 'background 160ms ease',
        }}
      >
        {text ? (
          <span
            style={{
              display: 'inline-block',
              padding: background !== 'transparent' ? '3px 9px' : 0,
              borderRadius: background !== 'transparent' ? 999 : 0,
              background,
            }}
          >
            {text}
          </span>
        ) : (
          '—'
        )}
      </td>
    );
  };

  if (loading) {
    return (
      <LoadingSpinner title="Loading DOA" subtitle="Fetching DOA records..." />
    );
  }

  if (error) {
    return (
      <div
        style={{
          height: '100%',
          background: PAGE.colors.bg,
          fontFamily: PAGE.fontFamily,
          overflow: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            maxWidth: PAGE.maxWidth,
            margin: '0 auto',
            padding: `${PAGE.paddingY}px ${PAGE.paddingX}px`,
            boxSizing: 'border-box',
          }}
        >
          <PageError message={error} />
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* ----------------------------------------
           1. COMPACT TOOLBAR
        ----------------------------------------- */
        .doa-compact-toolbar > div {
          padding: 8px 10px !important;
          gap: 8px !important;
          border-radius: 12px !important;
          margin: 0 !important;
        }

        .doa-compact-toolbar input[type="text"] {
          height: 34px !important;
          min-width: 180px !important;
          padding: 0 10px !important;
          font-size: 12px !important;
        }

        .doa-compact-toolbar input[type="date"] {
          height: 34px !important;
          width: 130px !important;
          padding: 0 8px !important;
          font-size: 12px !important;
        }

        .doa-compact-toolbar button {
          min-height: 34px !important;
          height: 34px !important;
          padding: 0 10px !important;
          font-size: 11px !important;
          font-weight: 700 !important;
        }

        .doa-compact-toolbar span {
          font-size: 11px !important;
        }

        /* ----------------------------------------
           2. STICKY PROFESSIONAL TABLE HEADER
        ----------------------------------------- */
        .doa-page table {
          width: 100% !important;
          min-width: 1000px !important;
          border-radius: 0 !important;
          table-layout: auto !important;
          border-collapse: separate !important;
          border-spacing: 0 !important;
        }

        .doa-page thead th,
        .doa-page th {
          position: sticky !important;
          top: 0 !important;
          z-index: 8 !important;
          background: #f8fafc !important;
          color: #334155 !important;
          padding: 9px 12px !important;
          font-size: 11px !important;
          letter-spacing: 0.7px !important;
          white-space: nowrap !important;
          text-transform: uppercase !important;
          border-bottom: 1px solid #dbe3ef !important;
          box-shadow: 0 1px 0 rgba(15, 23, 42, 0.06) !important;
        }

        /* ----------------------------------------
           3. ZEBRA ROWS + SMOOTH ROW HOVER
        ----------------------------------------- */
        .doa-page tbody tr:nth-child(odd) td {
          background: #ffffff;
        }

        .doa-page tbody tr:nth-child(even) td {
          background: #f9fbfd;
        }

        .doa-page tbody tr:hover td {
          background: #eef6ff !important;
        }

        .doa-page tbody tr td {
          border-bottom: 1px solid #edf2f7 !important;
        }

        .doa-page td {
          padding: 8px 12px !important;
          font-size: 12px !important;
          white-space: nowrap !important;
        }

        .doa-page thead,
        .doa-page tbody,
        .doa-page tr,
        .doa-page th,
        .doa-page td {
          border-radius: 0 !important;
        }
      `}</style>

      {refreshing && (
        <LoadingSpinner
          title="Refreshing DOA"
          subtitle="Updating latest DOA records..."
        />
      )}

      <div
        className="doa-page"
        style={{
          height: '100%',
          minHeight: 0,
          background: PAGE.colors.bg,
          fontFamily: PAGE.fontFamily,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            maxWidth: PAGE.maxWidth,
            height: '100%',
            margin: '0 auto',
            padding: '12px 20px 12px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {/* Compact toolbar */}
          <div
            className="doa-compact-toolbar"
            style={{
              flexShrink: 0,
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <FilterToolbar
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search DOA items…"
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
                dateMin={allDates?.[0]}
                dateMax={allDates?.[allDates.length - 1]}
                showClear={Boolean(searchTerm || dateFrom || dateTo)}
                onClear={clearFilters}
                refreshing={refreshing}
                onRefresh={refreshData}
              />
            </div>
          </div>

          {/* Full-page attached table area */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <HoverNav
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabledPrev={page === 1}
              disabledNext={page === totalPages}
            >
              <DataTableCard
                style={{
                  flex: 1,
                  height: '100%',
                  minHeight: 0,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 10,
                  boxShadow: 'none',
                }}
                footer={
                  <PaginationBar
                    page={page}
                    totalPages={totalPages}
                    totalItems={filteredData.length}
                    itemLabel="total"
                    disabled={refreshing}
                    onPrevious={() => setPage((p) => Math.max(1, p - 1))}
                    onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                  />
                }
              >
                {filteredData.length === 0 ? (
                  <div
                    style={{
                      padding: 48,
                      textAlign: 'center',
                      color: PAGE.colors.muted,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: PAGE.colors.heading,
                      }}
                    >
                      No DOA items found
                    </div>

                    <p style={{ ...PAGE.type.pageSubtitle, marginTop: 8 }}>
                      All items are in good condition.
                    </p>
                  </div>
                ) : (
                  <SheetDataTable
                    headers={headers}
                    rows={paginatedData}
                    renderHeader={formatDoaHeader}
                    renderCell={renderDoaCell}
                    minWidth={1000}
                    cellFontSize={12}
                  />
                )}
              </DataTableCard>
            </HoverNav>
          </div>
        </div>
      </div>
    </>
  );
}